import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

const PRODUCT_OPTIONS = [
  { value: 'all', label: 'All products' },
  { value: 'energy', label: 'Energy' },
  { value: 'metals', label: 'Metals' },
  { value: 'oil', label: 'Oil' },
  { value: 'lng', label: 'LNG' },
  { value: 'power', label: 'Power' },
  { value: 'gas', label: 'Gas' },
  { value: 'softs_ags', label: 'Agriculture & Softs' },
  { value: 'freight_shipping', label: 'Freight & Shipping' },
  { value: 'cross_commodity', label: 'Cross-Commodity' },
  { value: 'carbon_renewables', label: 'Carbon & Renewables' },
];

const MODE_OPTIONS = [
  { value: 'hires', label: 'Hires (inbound)' },
  { value: 'leavers', label: 'Leavers (outbound)' },
  { value: 'net', label: 'Net flow' },
];

const PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#eab308',
  '#22c55e', '#14b8a6', '#3b82f6', '#ef4444', '#a855f7',
  '#84cc16', '#06b6d4',
];

/** Match default force sizing so links still meet the node edge. */
function getNodeRadius(node) {
  return Math.sqrt(Math.max(node.val || 1, 0.5)) * 4.5;
}

function initialsFromLabel(name) {
  if (!name || !String(name).trim()) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] || '';
    const b = parts[parts.length - 1][0] || '';
    return `${a}${b}`.toUpperCase().slice(0, 2);
  }
  return String(name).slice(0, 2).toUpperCase();
}

export default function MarketIntelligenceGraph() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [graphPayload, setGraphPayload] = useState(null);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [product, setProduct] = useState('energy');
  const [mode, setMode] = useState('hires');
  const [minConfidence, setMinConfidence] = useState(50);
  const [minEdgeWeight, setMinEdgeWeight] = useState(2);
  const [maxNodes, setMaxNodes] = useState(250);
  const [location, setLocation] = useState('');
  /** Full-width graph: hide filter + leaderboard columns. */
  const [vizOnly, setVizOnly] = useState(false);
  /** Selected flow edge → show sample moves panel (from API edge payload). */
  const [selectedEdge, setSelectedEdge] = useState(null);

  /** ForceGraph measures parent at paint; flex parents often report 0×0 → blank canvas without explicit size. */
  const graphWrapRef = useRef(null);
  /** label → HTMLImageElement | 'error' | 'loading' */
  const logoCacheRef = useRef(new Map());
  const [logoEpoch, setLogoEpoch] = useState(0);
  const [graphSize, setGraphSize] = useState({ width: 920, height: 560 });

  const measureGraph = useCallback(() => {
    const el = graphWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.max(200, Math.floor(r.width));
    const h = Math.max(200, Math.floor(r.height));
    if (w > 0 && h > 0) {
      setGraphSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    }
  }, []);

  useLayoutEffect(() => {
    measureGraph();
    const el = graphWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measureGraph());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureGraph]);

  useEffect(() => {
    measureGraph();
  }, [graphPayload, measureGraph]);

  useEffect(() => {
    measureGraph();
  }, [vizOnly, measureGraph]);

  useEffect(() => {
    setSelectedEdge(null);
  }, [graphPayload]);

  /** Load company logos (Logo.dev / fallback via /api/company-logo) for canvas draw. */
  useEffect(() => {
    if (!graphPayload?.nodes?.length) return;
    const labels = [...new Set(graphPayload.nodes.map((n) => n.label).filter(Boolean))];
    const cache = logoCacheRef.current;

    labels.forEach((label) => {
      if (cache.has(label)) return;
      cache.set(label, 'loading');

      const url = `${API_BASE_URL}/api/company-logo/${encodeURIComponent(label)}`;
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          const logoUrl = data.logo_url;
          if (!logoUrl) {
            cache.set(label, 'error');
            setLogoEpoch((e) => e + 1);
            return;
          }
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            cache.set(label, img);
            setLogoEpoch((e) => e + 1);
          };
          img.onerror = () => {
            cache.set(label, 'error');
            setLogoEpoch((e) => e + 1);
          };
          img.src = logoUrl;
        })
        .catch(() => {
          cache.set(label, 'error');
          setLogoEpoch((e) => e + 1);
        });
    });
  }, [graphPayload]);

  const paintNodeCanvas = useCallback(
    (node, ctx, globalScale) => {
      void logoEpoch; // re-paint when logo cache updates (ref alone does not re-render)
      const label = node.label || '';
      const r = getNodeRadius(node);
      const cached = logoCacheRef.current.get(label);
      const img = cached instanceof HTMLImageElement ? cached : null;

      if (img && img.complete && img.naturalWidth) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.clip();
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const cover = Math.max((2 * r) / iw, (2 * r) / ih);
        const dw = iw * cover;
        const dh = ih * cover;
        ctx.drawImage(img, node.x - dw / 2, node.y - dh / 2, dw, dh);
        ctx.restore();
      } else {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
        ctx.fillStyle = node.color || '#6366f1';
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.font = `${Math.max(8, r * 0.85) / globalScale}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initialsFromLabel(label), node.x, node.y);
      }

      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(200, 208, 218, 0.95)';
      ctx.lineWidth = 1.35 / globalScale;
      ctx.stroke();

      ctx.font = `${11 / globalScale}px system-ui, sans-serif`;
      ctx.fillStyle = '#334155';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      let display = label;
      if (display.length > 24) display = `${display.slice(0, 22)}…`;
      ctx.fillText(display, node.x, node.y + r + 3 / globalScale);
    },
    [logoEpoch]
  );

  const nodePointerArea = useCallback((node, ctx, globalScale) => {
    const r = getNodeRadius(node);
    ctx.beginPath();
    ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
  }, []);

  const getAuthToken = useCallback(async () => {
    if (currentUser && typeof currentUser.getIdToken === 'function') {
      return currentUser.getIdToken();
    }
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('firebaseToken') ||
      localStorage.getItem('idToken') ||
      ''
    );
  }, [currentUser]);

  const fetchGraph = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        product,
        mode,
        min_confidence: String(minConfidence),
        min_edge_weight: String(minEdgeWeight),
        max_nodes: String(maxNodes),
        max_edges: '800',
      });
      if (location.trim()) {
        params.set('location', location.trim());
      }
      const token = await getAuthToken();
      const res = await fetch(
        `${API_BASE_URL}/api/market-intelligence/graphs/company-flows?${params.toString()}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      if (!data.success) {
        throw new Error(data.error || 'Request failed');
      }
      setGraphPayload(data);
    } catch (e) {
      setError(e.message || String(e));
      setGraphPayload(null);
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    product,
    mode,
    minConfidence,
    minEdgeWeight,
    maxNodes,
    location,
    getAuthToken,
  ]);

  useEffect(() => {
    fetchGraph();
  }, []);

  const graphData = useMemo(() => {
    if (!graphPayload?.nodes?.length) {
      return { nodes: [], links: [] };
    }
    const modeMeta = graphPayload.meta?.mode || 'hires';
    const nodes = graphPayload.nodes.map((n) => {
      // ForceGraph scales radius from val (~√val); keep floor so small counts stay visible.
      let raw = 1;
      if (modeMeta === 'hires') raw = Math.max(1, n.inbound || 0);
      else if (modeMeta === 'leavers') raw = Math.max(1, n.outbound || 0);
      else raw = Math.max(1, Math.abs(n.net || 0));
      const val = Math.max(2, raw * 1.15);
      return {
        ...n,
        id: n.id,
        label: n.label,
        val,
        color: PALETTE[(n.community_id || 0) % PALETTE.length],
      };
    });
    const links = (graphPayload.edges || []).map((e) => ({
      source: e.source,
      target: e.target,
      weight: e.weight,
      sample_moves: Array.isArray(e.sample_moves) ? e.sample_moves : [],
      source_label: e.source_label || '',
      target_label: e.target_label || '',
      edge_id: e.id || `${e.source_label}|${e.target_label}`,
    }));
    return { nodes, links };
  }, [graphPayload]);

  const edgeSelectionKey = selectedEdge?.edge_id || null;
  const isLinkSelected = useCallback(
    (link) => edgeSelectionKey && link.edge_id === edgeSelectionKey,
    [edgeSelectionKey]
  );

  const handleLinkClick = useCallback((link) => {
    setSelectedEdge({
      edge_id: link.edge_id,
      source_label: link.source_label,
      target_label: link.target_label,
      weight: link.weight,
      sample_moves: Array.isArray(link.sample_moves) ? link.sample_moves : [],
    });
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setSelectedEdge(null);
  }, []);

  const meta = graphPayload?.meta;

  return (
    <div className="flex flex-col xl:flex-row gap-4 xl:items-stretch relative">
      {!vizOnly && (
      <div className="w-full xl:w-64 shrink-0 space-y-3">
        <div className="grid grid-cols-1 gap-2 text-sm">
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">Start</span>
            <input
              type="date"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">End</span>
            <input
              type="date"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">Product</span>
            <select
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            >
              {PRODUCT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">Mode</span>
            <select
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              {MODE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">Min confidence</span>
            <input
              type="number"
              min={0}
              max={100}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={minConfidence}
              onChange={(e) => setMinConfidence(Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">Min edge weight</span>
            <input
              type="number"
              min={1}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={minEdgeWeight}
              onChange={(e) => setMinEdgeWeight(Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">Max nodes</span>
            <input
              type="number"
              min={10}
              max={500}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={maxNodes}
              onChange={(e) => setMaxNodes(Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className="text-gray-600 dark:text-gray-400">Location (optional)</span>
            <input
              type="text"
              placeholder="e.g. Singapore"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </label>
        </div>
        <button
          type="button"
          onClick={fetchGraph}
          disabled={loading}
          className="w-full py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh graph'}
        </button>
      </div>
      )}

      <div
        ref={graphWrapRef}
        className={`flex-1 min-w-0 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden relative ${
          vizOnly
            ? 'min-h-[70vh] h-[min(92vh,980px)] xl:min-h-[720px]'
            : 'min-h-[480px] h-[min(78vh,880px)] xl:min-h-[560px]'
        }`}
      >
        <div className="absolute top-2 right-2 z-30 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setVizOnly((v) => !v)}
            className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {vizOnly ? 'Show sidebars' : 'Visualization only'}
          </button>
        </div>
        {meta?.graph_simplified && (
          <div className="absolute top-2 left-2 z-10 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-900 dark:text-amber-100 px-2 py-1 rounded">
            Graph simplified (filters applied)
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-20 bg-gray-50/80 dark:bg-gray-900/80 text-sm text-gray-600 dark:text-gray-400">
            Loading graph…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        {!error && graphData.nodes.length === 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm p-4 text-center">
            No edges for these filters. Try widening the date range or lowering <strong>min edge weight</strong> to 1 to
            include single-move routes.
          </div>
        )}
        {!error && graphData.nodes.length > 0 && (
          <ForceGraph2D
            width={graphSize.width}
            height={graphSize.height}
            graphData={graphData}
            nodeLabel="label"
            nodeVal="val"
            nodeRelSize={11}
            nodeCanvasObjectMode={() => 'replace'}
            nodeCanvasObject={paintNodeCanvas}
            nodePointerArea={nodePointerArea}
            linkDirectionalArrowLength={6}
            linkDirectionalArrowRelPos={1}
            linkWidth={(l) => {
              const base = Math.sqrt(1 + (l.weight || 1)) * 0.8;
              return isLinkSelected(l) ? base * 1.85 : base;
            }}
            linkColor={(l) =>
              isLinkSelected(l) ? 'rgba(249,115,22,0.95)' : 'rgba(100,116,139,0.55)'
            }
            cooldownTicks={120}
            onEngineStop={() => {}}
            onLinkClick={handleLinkClick}
            onBackgroundClick={handleBackgroundClick}
          />
        )}
        {selectedEdge && (
          <div className="absolute bottom-3 left-3 right-3 md:left-auto md:right-3 md:max-w-md z-40 rounded-xl border border-gray-200 dark:border-gray-600 bg-white/98 dark:bg-gray-900/98 shadow-lg backdrop-blur-sm text-sm text-gray-800 dark:text-gray-100 max-h-[min(42vh,360px)] flex flex-col">
            <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700 shrink-0">
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Flow
                </p>
                <p className="font-semibold text-gray-900 dark:text-white leading-snug">
                  <span className="break-words">{selectedEdge.source_label || '—'}</span>
                  <span className="mx-1 text-orange-500">→</span>
                  <span className="break-words">{selectedEdge.target_label || '—'}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {selectedEdge.weight != null ? (
                    <>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{selectedEdge.weight}</span> moves
                      in range
                    </>
                  ) : (
                    'Moves in range'
                  )}
                  {' · '}
                  Sample below (up to 5)
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEdge(null)}
                className="shrink-0 rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 text-lg leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto px-3 py-2 space-y-3">
              {(selectedEdge.sample_moves || []).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-xs">
                  No sample names on this edge. Lower min edge weight or widen dates to include more moves.
                </p>
              ) : (
                (selectedEdge.sample_moves || []).map((m, i) => (
                  <div
                    key={`${m.name}-${m.date}-${i}`}
                    className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/50 px-2.5 py-2"
                  >
                    <p className="font-medium text-gray-900 dark:text-white">
                      {m.name || 'Unknown'}
                    </p>
                    {m.role ? (
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{m.role}</p>
                    ) : null}
                    {m.date ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{m.date}</p>
                    ) : null}
                    {m.source_url ? (
                      <a
                        href={m.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-orange-600 dark:text-orange-400 hover:underline mt-1 inline-block break-all"
                      >
                        Source
                      </a>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {!vizOnly && (
      <div className="w-full xl:w-72 shrink-0 space-y-4 text-sm max-h-[70vh] overflow-y-auto">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Top hirers</h3>
          <ul className="space-y-1">
            {(graphPayload?.leaderboards?.top_hirers || []).slice(0, 10).map((row, i) => (
              <li key={i} className="flex justify-between text-gray-700 dark:text-gray-300">
                <span className="truncate">{row.company}</span>
                <span className="font-medium">{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Top leavers</h3>
          <ul className="space-y-1">
            {(graphPayload?.leaderboards?.top_leavers || []).slice(0, 10).map((row, i) => (
              <li key={i} className="flex justify-between text-gray-700 dark:text-gray-300">
                <span className="truncate">{row.company}</span>
                <span className="font-medium">{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Top flows</h3>
          <ul className="space-y-1">
            {(graphPayload?.leaderboards?.top_flows || []).slice(0, 10).map((row, i) => (
              <li key={i} className="text-gray-700 dark:text-gray-300">
                <span className="truncate">{row.from}</span>
                <span className="mx-1">→</span>
                <span className="truncate">{row.to}</span>
                <span className="float-right font-medium">{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
        {meta && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
            Moves scanned: {meta.moves_scanned} · After filters: {meta.moves_after_filters}
            {meta.moves_missing_from_company ? ` · Missing from-company: ${meta.moves_missing_from_company}` : ''}
          </p>
        )}
      </div>
      )}
    </div>
  );
}
